/**
 * This package provides a list of validators
 * https://www.npmjs.com/package/validator
 */
import { VALIDATION } from "capitalrx-components-2.0/lib/constants/validation";
import cron from "cron-validate";
import moment from "moment";
import validator from "validator";

import { checkPasswordStrength } from "../checkPasswordStrength";
import { formatDateInput } from "../formatters";

const MIN_YEAR = 1990;
const DATE_WHEN_REPORTS_CAN_FIRST_BE_RUN = 1;

export const required = (value: string | number): string | undefined => {
  if (value || typeof value === "number") {
    return undefined;
  }

  return VALIDATION.REQUIRED;
};

export const maxLength =
  (max: number) =>
  (value: string): string | undefined => {
    if (value && value.length > max) {
      return `Must be ${max} characters or less`;
    }

    return undefined;
  };

export const minLength =
  (min: number) =>
  (value: string): string | undefined => {
    if (value && value.length < min) {
      return `Must be ${min} characters or more`;
    }

    return undefined;
  };

export const minMaxLengthBound =
  (min: number, max: number) =>
  (value: string): string | undefined => {
    if ((value && value.length < min) || (value && value.length > max)) {
      return `Must be min ${min} characters and max ${max} characters.`;
    }

    return undefined;
  };

export const isNumber = (value: string): string | undefined => {
  if (value && isNaN(Number(value))) {
    return "Must be a number.";
  }

  return undefined;
};

export const isPositiveInteger = (value: string): string | undefined => {
  const passesRegexTest = /^[1-9]\d*$/.test(value);
  if (value && !passesRegexTest) {
    return "Must be a non-zero positive integer";
  }

  return undefined;
};

export const isYear = (value: string): string | undefined => {
  if (value && (isNumber(value) !== undefined || value.length !== 4)) {
    return "Value is not a valid year";
  }

  return undefined;
};

export const minValue =
  (min: number) =>
  (value: number): string | undefined => {
    if (value && value < min) {
      return `Must be at least ${min}.`;
    }
    return undefined;
  };

export const email = (value: string): string | undefined => {
  if (value && !validator.isEmail(value)) {
    return "Invalid email address.";
  }
  return undefined;
};

export const phone = (value: string): string | undefined => {
  if (value && !validator.isMobilePhone(value, "en-US")) {
    return "Invalid phone number.";
  }
  return undefined;
};

export const alphaNumeric = (value: string): string | undefined => {
  if (value && /[^a-zA-Z0-9 ]/i.test(value)) {
    return "Only alphanumeric characters.";
  }

  return undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const passwordsMatch = (value: string, allValues: Record<string, any>): string | undefined => {
  if (value !== allValues.password) {
    return "Your passwords don't match.";
  }
  return undefined;
};

export const meetsPasswordRequirements = (password: string): string | undefined => {
  const passwordStrength = checkPasswordStrength(password);
  if (
    !passwordStrength.length
    || !passwordStrength.lowercase
    || !passwordStrength.number
    || !passwordStrength.specialChar
    || !passwordStrength.uppercase
  ) {
    const passwordArray: Array<string> = [];
    if (!passwordStrength.length) {
      passwordArray.push("at least 15 characters");
    }
    if (!passwordStrength.lowercase) {
      passwordArray.push("at least 1 lowercase letter");
    }
    if (!passwordStrength.number) {
      passwordArray.push("at least 1 number");
    }
    if (!passwordStrength.specialChar) {
      passwordArray.push("at least 1 special character");
    }
    if (!passwordStrength.uppercase) {
      passwordArray.push("at least 1 uppercase letter");
    }
    if (passwordArray.length > 1) {
      passwordArray[passwordArray.length - 1] = "and " + passwordArray[passwordArray.length - 1];
    }
    // if there are two unmet requirements, we do not want to use a comma
    if (passwordArray.length === 2) {
      return "Password must contain " + passwordArray.join(" ") + ".";
    } else {
      return "Password must contain " + passwordArray.join(", ") + ".";
    }
  } else {
    return undefined;
  }
};

// Once we get off cognito, whenever that is, we can delete this.
export const meetsAppPasswordRequirements = (password: string): string | undefined => {
  const passwordStrength = checkPasswordStrength(password);
  if (
    !passwordStrength.length
    || !passwordStrength.lowercase
    || !passwordStrength.number
    || !passwordStrength.specialChar
    || !passwordStrength.uppercase
    || !passwordStrength.trimmedWhiteSpace
  ) {
    const passwordArray: Array<string> = [];
    if (!passwordStrength.length) {
      passwordArray.push("at least 15 characters");
    }
    if (!passwordStrength.lowercase) {
      passwordArray.push("at least 1 lowercase letter");
    }
    if (!passwordStrength.number) {
      passwordArray.push("at least 1 number");
    }
    if (!passwordStrength.specialChar) {
      passwordArray.push("at least 1 special character");
    }
    if (!passwordStrength.uppercase) {
      passwordArray.push("at least 1 uppercase letter");
    }
    if (passwordArray.length > 1) {
      passwordArray[passwordArray.length - 1] = "and " + passwordArray[passwordArray.length - 1];
    }
    if (!passwordStrength.trimmedWhiteSpace) {
      passwordArray.push("no whitespace");
    }
    // if there are two unmet requirements, we do not want to use a comma
    if (passwordArray.length === 2) {
      return "Password must contain " + passwordArray.join(" ") + ".";
    } else {
      return "Password must contain " + passwordArray.join(", ") + ".";
    }
  } else {
    return undefined;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validStartDate = (_value: string, allValues: Record<string, any>, props: Record<string, any>): string | undefined => {
  const { effective_date } = props;
  const { start_date } = allValues;
  const validStartDate = moment(formatDateInput(start_date)).isSameOrAfter(effective_date);

  if (!validStartDate) {
    return "Invalid start date.";
  }
  return undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validEndDate = (_value: string, allValues: Record<string, any>, props: Record<string, any>): string | undefined => {
  const { termination_date } = props;
  const { end_date } = allValues;
  const validEndDate = moment(formatDateInput(end_date)).isSameOrBefore(termination_date);

  if (!validEndDate) {
    return "Invalid end date.";
  }
  return undefined;
};

export const validFee = (value: string): string | undefined => {
  if (value && isNaN(Number(value))) {
    return "Invalid fee amount";
  }

  return undefined;
};

export const validDiscount = (value: string): string | undefined => {
  if (value && isNaN(Number(value))) {
    return "Invalid discount amount";
  }

  return undefined;
};

export const validDateFormat = (value: string): string | undefined => {
  if (value === null || value === "") {
    return undefined;
  } else {
    const valid = moment(value, "MM/DD/YYYY", true).isValid();
    if (valid && value !== undefined) {
      return undefined;
    } else if (value === null) {
      return undefined;
    }

    return "Invalid date format";
  }
};

export const validDateAndNotFuture = (value: string): string | undefined => {
  if (value === null || value === "") {
    return undefined;
  } else {
    const inputDate = moment(value, "MM/DD/YYYY");
    const valid = inputDate.isValid();
    const today = moment();

    if (inputDate.isAfter(today)) {
      return "Date cannot be in the future.";
    }
    if (valid) {
      return undefined;
    }

    return "Invalid date format";
  }
};

const mostRecentValidDate = (): string => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  // If it's currently (or after) DATE_WHEN_REPORTS_CAN_FIRST_BE_RUN, last month is most recent
  if (now.getDate() >= DATE_WHEN_REPORTS_CAN_FIRST_BE_RUN) {
    if (currentMonth === 1) {
      return `${currentYear - 1}-12`;
    } else {
      return `${currentYear}-${currentMonth - 1}`;
    }
  } else {
    // Otherwise, two months ago is most recent
    if (currentMonth <= 2) {
      return `${currentYear - 1}-${currentMonth + 10}`;
    } else {
      return `${currentYear}-${currentMonth - 2}`;
    }
  }
};

const hasValidReportDate = (month: number, year: number): boolean => {
  const mostRecentValidFullDate = new Date(mostRecentValidDate());
  const requestedDate = new Date(`${year}-${month}`);
  return requestedDate <= mostRecentValidFullDate;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validReportPeriod = (value: string, allValues: Record<string, any>): string | undefined => {
  const currentYear = new Date().getFullYear();
  const { report_year, report_type } = allValues;
  let periodType = 1;
  let periodValue = value;

  if (report_type === "quarterly") {
    periodType = 3;
    if (value) periodValue = value.charAt(1);
  }
  const hasValidYear = Number(report_year) >= MIN_YEAR && Number(report_year) <= currentYear;
  const hasValidMonth = Number(periodValue) * periodType > 0 && Number(periodValue) * periodType < 13;

  if (!hasValidMonth || !report_year || !hasValidYear) {
    return undefined;
  }

  if (hasValidYear && hasValidMonth && hasValidReportDate(Number(periodValue) * periodType, Number(report_year))) {
    return undefined;
  }

  return `Invalid report period and year combination`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const validReportYear = (value: string, allValues: Record<string, any>): string | undefined => {
  const currentYear = new Date().getFullYear();
  const { report_period, report_type } = allValues;
  let periodType = 1;
  let periodValue = report_period;

  if (report_type === "quarterly") {
    periodType = 3;
    if (report_period) periodValue = report_period.charAt(1);
  }

  const hasValidYear = Number(value) >= MIN_YEAR && Number(value) <= currentYear;
  const hasValidMonth = Number(periodValue) * periodType > 0 && Number(periodValue) * periodType < 13;

  if (hasValidYear && !report_period) {
    return undefined;
  }

  if (hasValidMonth && hasValidYear) {
    if (hasValidReportDate(Number(periodValue) * periodType, Number(value))) return undefined;
    return " ";
  }

  return "Invalid Year";
};

export const validateZipcode = (value: string): string | undefined => {
  if (value && /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value)) {
    return undefined;
  }

  return "Invalid Zip Code";
};

export const cronValidate = (input: string): string | undefined => {
  // Need to provide a default value of string because cron-validate library will fail on initialization with null
  const value = input || "";

  const cronInput = cron(value, { preset: "aws-cloud-watch" });

  if (cronInput.isValid() || value === "") {
    return undefined;
  }

  const errorValue = cronInput.getError().join(". ");

  return errorValue;
};

export const validateJSON = (input: string): string | undefined => {
  try {
    JSON.parse(input);
  } catch (e) {
    return "Invalid JSON schema";
  }
  return undefined;
};

export const deleteConfirmation = (value: string): string | undefined => {
  if (value && value.toLowerCase() === "delete") {
    return undefined;
  }

  return "Please type in the word 'delete'";
};

export const outputKeyValidate = (input: string): string | undefined => {
  /**
   * An output key is a file path. This file path can accept variables within
   * braces, e.g., path/to/test_folder_1/file{date:YYYYMMDD}.{file_extension_variable}
   * This function validates {dateTimeKey:dateTimeFormat} while accepting all other braced values.
   */
  const validDateFormatKeys = ["date", "max_date", "min_date"];
  const validDateFormatValues = [
    "YYYYMMDD",
    "MMDDYYYY",
    "YYYYMMDDHHmm",
    "YYMMDD",
    "YYYYMMDDHHmmss",
    "HHmmss",
    "YYYY-MM-DD",
    "MM-DD-YYYY",
    "YY-MM-DD",
    "YYYYMMDDHHmmssSS",
    "HHmmssSS",
    "YYYYQQ",
  ];
  const errorMessage = "Must be an accepted date format";

  // this regex finds all instances of open close brackets, i.e., {}
  const regExBracedText = /\{(.*?)\}/g;
  let bracedTextFound;
  while ((bracedTextFound = regExBracedText.exec(input))) {
    const splitText = bracedTextFound[1].split(":");

    if (splitText.length === 2 && validDateFormatKeys.includes(splitText[0]) == false) {
      return errorMessage;
    } else if (splitText.length === 2 && validDateFormatKeys.includes(splitText[0]) == true) {
      if (validDateFormatValues.includes(splitText[1].replace(/\s+/g, "")) == false) {
        return errorMessage;
      }
      // user entered too many colons, which is incorrect
    } else if (splitText.length > 2) {
      return "Too many colons in variable";
      /**
       * this is the case where the user is passing a single variable in brackets.
       * For now, we can assume the variable is valid so we 'continue'. Later on
       * we may get a list of valid variables, and that validation can be handled here.
       */
    } else if (splitText.length === 1) {
      continue;
    }
  }

  return undefined;
};

export const inclusiveStartDateValidate =
  (exclusiveEnd: string | null, isDisplayDateRange: boolean) =>
  (inclusiveStart: string | null): string | undefined => {
    const inclusiveStartDate = moment(inclusiveStart, "MM/DD/YYYY") || null;
    const exclusiveEndDate = moment(exclusiveEnd, "MM/DD/YYYY") || null;

    /**
     * We want to only check for these things if the display date range checkbox is checked
     */
    if (isDisplayDateRange) {
      if (inclusiveStartDate && exclusiveEndDate) {
        const inclusiveStartDateGreaterThanOrEqualToEndDate = inclusiveStartDate.isSameOrAfter(exclusiveEndDate);
        if (inclusiveStartDateGreaterThanOrEqualToEndDate) {
          return "Start Date must be before the End Date";
        }
      }

      if (!inclusiveStartDate && exclusiveEndDate) {
        return "Start Date required";
      }
    }
    return undefined;
  };

export const exclusiveEndDateValidate =
  (inclusiveStart: string | null, isDisplayDateRange: boolean) =>
  (exclusiveEnd: string | null): string | undefined => {
    const inclusiveStartDate = moment(inclusiveStart, "MM/DD/YYYY") || null;
    const exclusiveEndDate = moment(exclusiveEnd, "MM/DD/YYYY") || null;

    /**
     * We want to only check for these things if the display date range checkbox is checked
     */
    if (isDisplayDateRange) {
      if (inclusiveStartDate && exclusiveEndDate) {
        const isGreaterThanOrEqualToStartDate = exclusiveEndDate.isSameOrBefore(inclusiveStartDate);
        if (isGreaterThanOrEqualToStartDate) {
          return "End Date must be after the Start Date";
        }
      }

      if (inclusiveStartDate && !exclusiveEndDate) {
        return "End Date required";
      }
    }

    return undefined;
  };

export const noSpaces = (value: string): string | undefined => {
  if (value && value.indexOf(" ") >= 0) {
    return "Cannot contain spaces.";
  }
  return undefined;
};

export const noSpacesCheck = (value: string | number): string | undefined => {
  if (value && String(value).indexOf(" ") >= 0) {
    return "Cannot contain spaces.";
  }
  return undefined;
};

export const validateSocialSecurityNumber = (ssn: string): string | undefined => {
  const formated = /^\d{3}-?\d{2}-?\d{4}$/;
  const isNumber = validator.isNumeric(ssn);
  const isValid = formated.test(ssn) || (isNumber && ssn.length === 9);

  if (ssn && !isValid) {
    return "Invalid social security number";
  }
  return undefined;
};

export const isValidTin = (tin: string): string | undefined => {
  const isValid = /^\d{9}$/.test(tin);

  if (tin && !isValid) {
    return "TIN must be exactly 9 digits";
  }
  return undefined;
};

export const isValidPositiveCurrencyValue = (value: string): string | undefined => {
  const regExp = /(?=.*\d)^(?:([0-9]{1,3}(?:,\d{3})*)|0)(?:\.\d{1,2})?$/;
  const regex = new RegExp(regExp);
  if (value && !regex.test(value)) {
    return "Invalid Amount";
  }
  return undefined;
};
